import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { NgxFileDropEntry } from 'ngx-file-drop';
import { UserDataService } from 'src/app/shared/providers/user-data.service';
import { flyGhost, prefersReducedMotion, visibleCenter } from '../render-drag';
import { collectDroppedFiles, collectInputFiles, fileExtension, formatBytes, RenderUploadFile } from '../render-upload.inspector';
import { RenderPrepareProgress, renderPickerAccept } from '../render-upload.prepare';
import { canInflate } from '../render-zip';
import { RENDER_MODEL_EXTENSIONS, RENDER_UPLOAD_LIMIT_BYTES } from '../render.data';

// An upload in the zone, as the host reports it.
export interface RenderZoneUpload {
  fileName: string;
  // A sample's thumbnail; a user's file shows its format on a dark tile instead.
  poster?: string;
  format: string;
  sentBytes: number;
  totalBytes: number;
  done: boolean;
  // A line under the bar (a user's file: nothing is actually sent in this build).
  note?: string;
  // The archive the files came out of: the bar keeps its unpacking leg, full.
  archive?: string;
}

// Several models in a drop, one to pick (render-upload.prepare.ts pickModel).
export interface RenderZoneChoice {
  // The archive or folder they sit in, when they share one.
  source?: string;
  models: ReadonlyArray<{ path: string; size: number }>;
  selected: string;
}

// From this many models on, the choice gets a filter.
const CHOICE_FILTER_FROM = 7;

let nextZoneId = 0;

// The place a model is dropped or picked. Two shapes of the same thing:
//   inline  — the dashed box on the upload step, with "Choose a file" / "Choose a folder";
//   overlay — a full-window target that appears while something is dragged over the page
//             (the landing, for signed-in users), like the library's fullsize-dragdrop.
// It collects files and, inline, also takes a sample dragged from the page (render-drag.ts) and
// shows what happens to a drop in the box itself (owner, 2026-09-30: "the drop lives in the
// zone"): an archive unpacking and then uploading as one card with a two-leg bar, the question
// which model when there are several, and a red edge when the drop is refused (the reason docks
// under it, in the host). Inspection and the flow happen in the host.
@Component({
  selector: 'app-render-drop-zone',
  templateUrl: './render-drop-zone.component.html',
  styleUrls: ['./render-drop-zone.component.scss'],
})
export class RenderDropZoneComponent implements OnChanges {
  @Input() mode: 'inline' | 'overlay' = 'inline';
  @Input() disabled = false;
  // The file name the page points the user at (a sample clicked or being dragged): the zone
  // lights up and asks for it by name.
  @Input() invite: string | null = null;
  // A sample of the page is being dragged — accept it like a file. ngx-file-drop refuses every
  // drag that starts inside the page, so the zone handles these itself.
  @Input() sampleDrag = false;
  @Input() upload: RenderZoneUpload | null = null;
  // An archive being opened and unpacked before the upload: the upload's card from the start.
  @Input() unpacking: RenderPrepareProgress | null = null;
  @Input() choice: RenderZoneChoice | null = null;
  // The drop was refused: the box gets a red edge, the reason docks under it.
  @Input() refused = false;
  // In place of the formats line (the texture a waiting model asks for).
  @Input() hint: string | null = null;
  // "How to include textures" after the formats line; it asks for the export guide.
  @Input() helpLink = false;
  @Output() helpRequested = new EventEmitter<void>();
  @Output() filesCollected = new EventEmitter<RenderUploadFile[]>();
  @Output() unpackCancelled = new EventEmitter<void>();
  @Output() sampleDropped = new EventEmitter<void>();
  @Output() uploadCancelled = new EventEmitter<void>();
  @Output() choiceSelected = new EventEmitter<string>();
  @Output() choiceConfirmed = new EventEmitter<void>();
  @Output() choiceDismissed = new EventEmitter<void>();

  @ViewChild('fileInput') private fileInput?: ElementRef<HTMLInputElement>;
  @ViewChild('folderInput') private folderInput?: ElementRef<HTMLInputElement>;

  // A ZIP is opened on the page (render-upload.prepare.ts) — offered only where the browser can
  // unpack one (DecompressionStream: Chrome 103, Firefox 113, Safari 16.4). An older browser is not
  // told about ZIPs; one dropped anyway still opens if it is stored, else it gets its own message.
  public readonly opensZip = canInflate();
  public readonly accept = renderPickerAccept([...RENDER_MODEL_EXTENSIONS, ...(this.opensZip ? ['.zip'] : [])]);
  // The Figma says 100 MB; the limit is still an open question, so the text follows the check.
  public readonly limitLabel = formatBytes(RENDER_UPLOAD_LIMIT_BYTES, 0);
  public readonly choiceId = `render-zone-choice-${nextZoneId++}`;
  public choiceFilter = '';
  public dragging = false;
  public collecting = false;
  public sampleOver = false;
  // The poster waits, hidden, while a thumbnail flies into its place.
  public arriving = false;

  // dragenter/dragleave fire for every child crossed; the zone is left when they even out.
  private sampleDragDepth = 0;

  constructor(
    public readonly userDataService: UserDataService,
    private readonly host: ElementRef<HTMLElement>,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // A new choice starts unfiltered.
    if (changes['choice'] && changes['choice'].previousValue?.models !== changes['choice'].currentValue?.models) {
      this.choiceFilter = '';
    }
  }

  // app.component sets userDataService.dragOver on every dragenter/dragover over the page and
  // clears it on dragleave; the overlay follows it.
  public get overlayVisible(): boolean {
    return this.mode === 'overlay' && !this.disabled && this.userDataService.dragOver;
  }

  public get percent(): number {
    return this.upload && this.upload.totalBytes ? Math.round((this.upload.sentBytes / this.upload.totalBytes) * 100) : 0;
  }

  // --- the card: an archive unpacking, then the upload ---

  public get carding(): boolean {
    return !!this.upload || !!this.unpacking;
  }

  // Two legs when the files come out of an archive: unpack, then upload.
  public get phased(): boolean {
    return !!this.unpacking || !!this.upload?.archive;
  }

  public get cardName(): string {
    return this.upload ? this.upload.fileName : this.unpacking?.name || '';
  }

  // The line under the bar. While an archive unpacks it holds the place the upload's note takes
  // ("From big.zip: 1 model and 3 textures."), so the box does not grow between the two.
  public get cardNote(): string {
    if (this.unpacking) {
      return `Taking the model and its textures out of ${this.unpacking.name}.`;
    }
    return this.upload?.note || '';
  }

  public get cardFormat(): string {
    return this.upload ? this.upload.format : 'ZIP';
  }

  public get cardStatus(): string {
    const unpacking = this.unpacking;
    if (!unpacking) {
      return this.uploadStatus;
    }
    if (unpacking.phase === 'open') {
      return `Opening ${unpacking.name}…`;
    }
    return `Unpacking · ${formatBytes(unpacking.doneBytes)} of ${formatBytes(unpacking.totalBytes)}`;
  }

  // The unpacking leg: filling while it unpacks, full once the upload runs.
  public get unpackPercent(): number {
    return this.unpacking ? (this.unpacking.phase === 'open' ? 0 : this.unpacking.percent) : 100;
  }

  public get opening(): boolean {
    return this.unpacking?.phase === 'open';
  }

  public get cardPercent(): number {
    return this.upload ? this.percent : this.unpackPercent;
  }

  public cancelCard(): void {
    if (this.upload) {
      this.uploadCancelled.emit();
    } else {
      this.unpackCancelled.emit();
    }
  }

  // --- the choice ---

  public get filtersChoice(): boolean {
    return (this.choice?.models.length || 0) >= CHOICE_FILTER_FROM;
  }

  public get shownChoices(): Array<{ path: string; name: string; folder: string; size: string; format: string }> {
    const query = this.choiceFilter.trim().toLowerCase();
    return (this.choice?.models || [])
      .filter((model) => !query || model.path.toLowerCase().includes(query))
      .map((model) => {
        const parts = model.path.split('/');
        const name = parts.pop() || model.path;
        // The folder under the archive's own ('pack/Grass Pack/bush.fbx' → 'Grass Pack').
        const folder = (this.choice?.source && parts[0] === this.choice.source.replace(/\.zip$/i, '') ? parts.slice(1) : parts).join('/');
        return { path: model.path, name, folder, size: formatBytes(model.size), format: fileExtension(name).slice(1).toUpperCase() };
      });
  }

  public get choiceTitle(): string {
    const count = this.choice?.models.length || 0;
    return `${count} models in ${this.choice?.source || 'these files'}`;
  }

  public get chosenName(): string {
    const path = this.choice?.selected || '';
    return path.split('/').pop() || path;
  }

  public get choiceFolders(): number {
    return new Set((this.choice?.models || []).map((model) => model.path.split('/').slice(0, -1).join('/'))).size;
  }

  public onChoiceFilter(event: Event): void {
    this.choiceFilter = (event.target as HTMLInputElement).value;
  }

  public trackByPath(index: number, option: { path: string }): string {
    return option.path;
  }

  public get uploadStatus(): string {
    if (!this.upload) {
      return '';
    }
    const total = formatBytes(this.upload.totalBytes);
    if (this.upload.done) {
      return `Uploaded · ${total}. Starting your preview…`;
    }
    const sent = this.upload.sentBytes > 0 ? formatBytes(this.upload.sentBytes) : '0 MB';
    return `Uploading… ${sent} of ${total} · ${this.percent}%`;
  }

  public onFileOver(): void {
    this.dragging = true;
  }

  public onFileLeave(): void {
    this.dragging = false;
    if (this.mode === 'overlay') {
      this.userDataService.dragOver = false;
    }
  }

  public async onDropped(entries: NgxFileDropEntry[]): Promise<void> {
    this.dragging = false;
    this.userDataService.dragOver = false;
    if (this.disabled) {
      return;
    }
    this.collecting = true;
    try {
      this.filesCollected.emit(await collectDroppedFiles(entries));
    } finally {
      this.collecting = false;
    }
  }

  public chooseFile(): void {
    this.fileInput?.nativeElement.click();
  }

  public chooseFolder(): void {
    this.folderInput?.nativeElement.click();
  }

  public onInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = collectInputFiles(input.files);
    // Same file again must fire change again.
    input.value = '';
    if (files.length) {
      this.filesCollected.emit(files);
    }
  }

  // --- a sample dragged from the page ---

  @HostListener('dragenter', ['$event'])
  public onSampleDragEnter(event: DragEvent): void {
    if (!this.acceptsSample) {
      return;
    }
    event.preventDefault();
    this.sampleDragDepth++;
    this.sampleOver = true;
  }

  @HostListener('dragover', ['$event'])
  public onSampleDragOver(event: DragEvent): void {
    if (!this.acceptsSample) {
      return;
    }
    // Without this the drop is refused.
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
  }

  @HostListener('dragleave')
  public onSampleDragLeave(): void {
    if (!this.acceptsSample) {
      return;
    }
    this.sampleDragDepth = Math.max(0, this.sampleDragDepth - 1);
    this.sampleOver = this.sampleDragDepth > 0;
  }

  @HostListener('drop', ['$event'])
  public onSampleDrop(event: DragEvent): void {
    if (!this.acceptsSample) {
      return;
    }
    event.preventDefault();
    this.resetSampleDrag();
    this.sampleDropped.emit();
  }

  // Released anywhere, or cancelled with Escape.
  @HostListener('document:dragend')
  public resetSampleDrag(): void {
    this.sampleDragDepth = 0;
    this.sampleOver = false;
  }

  // --- motion ---

  // Click on a sample: its thumbnail glides into the zone and fades — how the drag goes.
  public playDragDemo(image: HTMLImageElement): void {
    const target = this.target;
    if (!target || prefersReducedMotion()) {
      return;
    }
    flyGhost(image, visibleCenter(target), 'demo');
  }

  // The drop done for the user: call right after the host has set `upload`. The thumbnail flies
  // into the upload's poster, which shows once it has landed.
  public async receive(image: HTMLImageElement): Promise<void> {
    if (prefersReducedMotion()) {
      return;
    }
    this.arriving = true;
    // The upload view is rendered by the change detection that follows the host's handler.
    await new Promise((resolve) => requestAnimationFrame(resolve));
    const poster = this.host.nativeElement.querySelector<HTMLElement>('.zone-upload__poster');
    if (!poster) {
      this.arriving = false;
      return;
    }
    const rect = poster.getBoundingClientRect();
    await flyGhost(image, { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2, size: rect.width }, 'drop', () => {
      // Straight on the element as well: the ghost goes in this frame, the poster must not wait
      // for change detection.
      poster.classList.remove('is-arriving');
      this.arriving = false;
    });
  }

  private get acceptsSample(): boolean {
    return this.mode === 'inline' && this.sampleDrag && !this.disabled && !this.carding;
  }

  private get target(): HTMLElement | null {
    return this.host.nativeElement.querySelector<HTMLElement>('.zone__target');
  }
}
